import React, { useState } from 'react';
import { X, Search, Plus, Building2 } from 'lucide-react';
import { useBusiness } from '../contexts/BusinessContext';
import { createStockItem } from '../lib/stockItems';
import { getSuppliers, createSupplier, Customer } from '../lib/customers';
import { getStockCategories, createStockCategory, StockCategory } from '../lib/stockCategories';

const STOCK_UNITS = {
  quantity: [
    { value: 'unit', label: 'Unit' },
    { value: 'box', label: 'Box' },
    { value: 'pack', label: 'Pack' },
    { value: 'set', label: 'Set' }
  ],
  length: [
    { value: 'ft', label: 'Foot (ft)' },
    { value: 'm', label: 'Meter (m)' }
  ],
  volume: [
    { value: 'gal', label: 'Gallon (gal)' },
    { value: 'l', label: 'Liter (L)' }
  ]
};

interface NewStockItemModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

export default function NewStockItemModal({
  isOpen,
  onClose,
  onSuccess
}: NewStockItemModalProps) {
  const { selectedBusiness } = useBusiness();
  const [isAnimating, setIsAnimating] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [categories, setCategories] = useState<StockCategory[]>([]);
  const [categorySearch, setCategorySearch] = useState('');
  const [isCategoryDropdownOpen, setIsCategoryDropdownOpen] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [isAddingNewCategory, setIsAddingNewCategory] = useState(false);
  const [newCategoryName, setNewCategoryName] = useState('');
  const [suppliers, setSuppliers] = useState<Customer[]>([]);
  const [selectedSupplier, setSelectedSupplier] = useState<string | null>(null);
  const [isAddingSupplier, setIsAddingSupplier] = useState(false);
  const [supplierFormData, setSupplierFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: ''
  });
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    acquisition_cost: '0',
    price: '0',
    sku: '',
    unit: 'unit',
    quantity: '0',
    reorder_point: '0',
    max_quantity: '100'
  });

  React.useEffect(() => {
    if (isOpen) {
      setIsAnimating(true);
      loadCategories();
      loadSuppliers();
    }
  }, [isOpen]);

  const loadCategories = async () => {
    if (!selectedBusiness?.id) return;

    try {
      const data = await getStockCategories(selectedBusiness.id);
      setCategories(data);
    } catch (err) {
      console.error('Error loading categories:', err);
      setError(err instanceof Error ? err.message : 'Failed to load categories');
    }
  };

  const loadSuppliers = async () => {
    if (!selectedBusiness?.id) return;

    try {
      const data = await getSuppliers(selectedBusiness.id);
      setSuppliers(data);
    } catch (err) {
      console.error('Error loading suppliers:', err);
      setError(err instanceof Error ? err.message : 'Failed to load suppliers');
    }
  };

  const handleTransitionEnd = () => {
    if (!isOpen) {
      setIsAnimating(false);
      setFormData({
        name: '',
        acquisition_cost: '',
        price: '',
        description: '',
        sku: '',
        unit: 'unit',
        quantity: '0',
        reorder_point: '0',
        max_quantity: '100'
      });
      setSelectedCategory(null);
      setIsAddingNewCategory(false);
      setNewCategoryName('');
      setCategorySearch('');
      setSelectedSupplier(null);
      setIsAddingSupplier(false);
      setSupplierFormData({
        first_name: '',
        last_name: '',
        email: '',
        phone: ''
      });
      setError(null);
    }
  };

  const handleAddCategory = async () => {
    if (!selectedBusiness?.id || !newCategoryName.trim()) return;

    try {
      setLoading(true);
      const newCategory = await createStockCategory({
        business_id: selectedBusiness.id,
        name: newCategoryName,
        description: null,
        order_index: categories.length
      });

      await loadCategories();
      setSelectedCategory(newCategory.id);
      setIsAddingNewCategory(false);
      setNewCategoryName('');
      setCategorySearch('');
    } catch (err) {
      console.error('Error creating category:', err);
      setError(err instanceof Error ? err.message : 'Failed to create category');
    } finally {
      setLoading(false);
    }
  };

  const handleAddSupplier = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedBusiness?.id) return;

    try {
      setLoading(true);
      setError(null);

      const supplier = await createSupplier({
        business_id: selectedBusiness.id,
        type: 'person',
        first_name: supplierFormData.first_name,
        last_name: supplierFormData.last_name,
        email: supplierFormData.email || null,
        phone: supplierFormData.phone || null,
        address: null,
        source: null,
        tags: [],
        last_interaction: null,
        lifetime_value: 0,
        next_service_date: null,
        referral_source: null,
        preferences: {},
        status: 'active',
        amount_spent: 0,
        credit_balance: 0,
        notes: null,
        tax_exempt: false,
        workflow_exempt: false,
        carfax_exempt: false,
        receive_reminders: false,
        receive_offers: false
      });

      await loadSuppliers();
      setSelectedSupplier(supplier.id);
      setIsAddingSupplier(false);
      setSupplierFormData({
        first_name: '',
        last_name: '',
        email: '',
        phone: ''
      });
    } catch (err) {
      console.error('Error creating supplier:', err);
      setError(err instanceof Error ? err.message : 'Failed to create supplier');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedBusiness?.id) {
      setError('No business selected');
      return;
    }

    if (!selectedCategory && !isAddingNewCategory) {
      setError('Please select or create a category');
      return;
    }

    let categoryId = selectedCategory;

    // Create new category if needed
    if (isAddingNewCategory && newCategoryName) {
      try {
        const newCategory = await createStockCategory({
          business_id: selectedBusiness.id,
          name: newCategoryName,
          description: null,
          order_index: categories.length
        });
        categoryId = newCategory.id;
      } catch (err) {
        console.error('Error creating category:', err);
        setError(err instanceof Error ? err.message : 'Failed to create category');
        return;
      }
    }

    try {
      setLoading(true);
      setError(null);

      const maxQuantity = parseInt(formData.max_quantity);
      const reorderPoint = parseInt(formData.reorder_point);
      
      if (maxQuantity <= reorderPoint || maxQuantity < 1) {
        throw new Error('Maximum quantity must be greater than reorder point');
      }

      await createStockItem({
        business_id: selectedBusiness.id,
        name: formData.name,
        acquisition_cost: parseFloat(formData.acquisition_cost) || 0,
        price: parseFloat(formData.price) || 0,
        description: formData.description || null,
        category_id: categoryId,
        supplier_id: selectedSupplier || null,
        unit: formData.unit,
        sku: formData.sku || null,
        quantity: parseInt(formData.quantity) || 0,
        reorder_point: parseInt(formData.reorder_point) || 0,
        max_quantity: maxQuantity,
        is_sellable: false,
        tax_rate: 0
      });

      onSuccess();
    } catch (err) {
      console.error('Error creating stock item:', err);
      setError(err instanceof Error ? err.message : 'Failed to create stock item. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen && !isAnimating) return null;

  return (
    <div 
      className={`fixed inset-0 bg-black transition-opacity duration-300 ${
        isOpen ? 'bg-opacity-50' : 'bg-opacity-0'
      } z-[10000]`}
      onClick={onClose}
    >
      <div 
        className={`fixed inset-y-0 right-0 w-full md:w-[600px] bg-white transform transition-transform duration-300 ease-out ${
          isOpen ? 'translate-x-0' : 'translate-x-full'
        }`}
        onTransitionEnd={handleTransitionEnd}
        onClick={e => e.stopPropagation()}
      >
        <div className="h-full flex flex-col">
          <div className="px-6 py-4 border-b border-[#2f2f2f] bg-[#1a1a1a] flex items-center justify-between">
            <h2 className="text-lg font-semibold text-white">New Stock Item</h2>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-300">
              <X className="h-5 w-5" />
            </button>
          </div>

          <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-6">
            <div className="space-y-8">
              {error && (
                <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-md text-sm">
                  {error}
                </div>
              )}

              {/* Basic Information */}
              <div>
                <h3 className="text-sm font-medium text-gray-900 mb-6">Basic Information</h3>
                <div className="space-y-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Item Name
                    </label>
                    <input
                      type="text"
                      required
                      value={formData.name}
                      onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                      className="w-full h-10 rounded-md border-gray-300 shadow-sm focus:border-gray-900 focus:ring-gray-900 text-sm"
                      placeholder="Enter item name"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Item Cost
                    </label>
                    <div className="relative">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <span className="text-gray-500 sm:text-sm">$</span>
                      </div>
                      <input
                        type="number"
                        step="0.01"
                        min="0"
                        value={formData.acquisition_cost}
                        onChange={(e) => {
                          const cost = e.target.value;
                          setFormData(prev => ({
                            ...prev,
                            acquisition_cost: cost,
                            price: cost // Auto-populate price with cost
                          }));
                        }}
                        className="w-full pl-7 pr-4 h-10 rounded-md border-gray-300 shadow-sm focus:border-gray-900 focus:ring-gray-900 text-sm"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Retail Price
                    </label>
                    <div className="relative">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <span className="text-gray-500 sm:text-sm">$</span>
                      </div>
                      <input
                        type="number"
                        step="0.01"
                        min="0"
                        value={formData.price}
                        onChange={(e) => setFormData(prev => ({ ...prev, price: e.target.value }))} 
                        className="w-full pl-7 pr-4 h-10 rounded-md border-gray-300 shadow-sm focus:border-gray-900 focus:ring-gray-900 text-sm"
                      />
                    </div>
                  </div>

                  {/* Category Selection */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Category
                    </label>
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                      <input
                        type="text"
                        required
                        placeholder="Search or select category..."
                        value={
                          selectedCategory
                            ? categories.find(c => c.id === selectedCategory)?.name || ''
                            : isAddingNewCategory
                            ? newCategoryName
                            : categorySearch
                        }
                        onChange={(e) => setCategorySearch(e.target.value)}
                        className="w-full pl-10 pr-4 py-2 text-sm bg-gray-50 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-transparent"
                        onClick={() => setIsCategoryDropdownOpen(true)}
                      />
                      {isCategoryDropdownOpen && (
                        <div className="absolute left-0 right-0 mt-1 max-h-48 overflow-y-auto bg-white rounded-md shadow-lg border border-gray-200 z-10">
                          {/* Show "Create Category" option */}
                          {categorySearch && (
                            <button
                              type="button"
                              onClick={() => {
                                setNewCategoryName(categorySearch);
                                setIsAddingNewCategory(true);
                                setIsCategoryDropdownOpen(false);
                              }}
                              className="w-full px-4 py-2 text-left text-sm text-blue-600 hover:bg-blue-50 font-medium"
                            >
                              <Plus className="h-4 w-4 inline mr-2" />
                              Create "{categorySearch}"
                            </button>
                          )}

                          {/* Show filtered categories */}
                          {categories
                            .filter(category => 
                              category.name.toLowerCase().includes(categorySearch.toLowerCase())
                            )
                            .map(category => (
                              <button
                                key={category.id}
                                type="button"
                                onClick={() => {
                                  setSelectedCategory(category.id);
                                  setIsAddingNewCategory(false);
                                  setCategorySearch('');
                                  setIsCategoryDropdownOpen(false);
                                }}
                                className="w-full px-4 py-2 text-left text-sm hover:bg-gray-50"
                              >
                                {category.name}
                              </button>
                            ))
                          }
                        </div>
                      )}
                    </div>
                    {isAddingNewCategory && (
                      <div className="mt-2">
                        <input
                          type="text"
                          value={newCategoryName}
                          onChange={(e) => setNewCategoryName(e.target.value)}
                          placeholder="Enter category name"
                          className="w-full h-9 rounded-md border-gray-300 shadow-sm focus:border-gray-900 focus:ring-gray-900 text-sm"
                        />
                        <div className="mt-2 flex justify-end space-x-2">
                          <button
                            type="button"
                            onClick={() => {
                              setIsAddingNewCategory(false);
                              setNewCategoryName('');
                            }}
                            className="px-2 py-1 text-xs text-gray-600 hover:text-gray-900"
                          >
                            Cancel
                          </button>
                          <button
                            type="button"
                            onClick={handleAddCategory}
                            disabled={!newCategoryName.trim() || loading}
                            className="px-2 py-1 text-xs text-blue-600 hover:text-blue-700 disabled:opacity-50"
                          >
                            {loading ? 'Creating...' : 'Create Category'}
                          </button>
                        </div>
                      </div>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Description
                    </label>
                    <textarea
                      value={formData.description}
                      onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                      rows={3}
                      className="w-full rounded-md border-gray-300 shadow-sm focus:border-gray-900 focus:ring-gray-900 text-sm"
                      placeholder="Enter item description"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      SKU
                    </label>
                    <input
                      type="text"
                      value={formData.sku}
                      onChange={(e) => setFormData(prev => ({ ...prev, sku: e.target.value }))}
                      className="w-full h-10 rounded-md border-gray-300 shadow-sm focus:border-gray-900 focus:ring-gray-900 text-sm"
                      placeholder="Enter SKU (optional)"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Current Stock Level
                    </label>
                    <input
                      type="number"
                      required
                      min="0"
                      value={formData.quantity}
                      onChange={(e) => setFormData(prev => ({ ...prev, quantity: e.target.value }))}
                      className="w-full h-10 rounded-md border-gray-300 shadow-sm focus:border-gray-900 focus:ring-gray-900 text-sm"
                      placeholder="0"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Reorder Point
                    </label>
                    <input
                      type="number"
                      required
                      min="0"
                      value={formData.reorder_point}
                      onChange={(e) => setFormData(prev => ({ ...prev, reorder_point: e.target.value }))}
                      className="w-full h-10 rounded-md border-gray-300 shadow-sm focus:border-gray-900 focus:ring-gray-900 text-sm"
                      placeholder="0"
                    />
                    <p className="mt-1 text-xs text-gray-500">
                      You'll be notified when stock falls below this level
                    </p>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Maximum Stock Level
                    </label>
                    <input
                      type="number"
                      required
                      min="1"
                      value={formData.max_quantity}
                      onChange={(e) => setFormData(prev => ({ ...prev, max_quantity: e.target.value }))}
                      className="w-full h-10 rounded-md border-gray-300 shadow-sm focus:border-gray-900 focus:ring-gray-900 text-sm"
                      placeholder="Enter maximum stock level"
                    />
                    <p className="mt-1 text-xs text-gray-500">
                      Maximum quantity to maintain in stock. Used for auto-ordering.
                    </p>
                  </div>
                </div>
              </div>

              {/* Supplier Selection */}
              <div>
                <h3 className="text-sm font-medium text-gray-900 mb-6">Supplier</h3>
                {isAddingSupplier ? (
                  <div className="space-y-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Supplier Name
                      </label>
                      <input
                        type="text"
                        required
                        value={supplierFormData.first_name + ' ' + supplierFormData.last_name}
                        onChange={(e) => {
                          const [firstName, lastName] = e.target.value.split(' ');
                          setSupplierFormData(prev => ({ ...prev, first_name: firstName, last_name: lastName }));
                        }}
                        className="w-full h-10 rounded-md border-gray-300 shadow-sm focus:border-gray-900 focus:ring-gray-900 text-sm"
                        placeholder="Enter supplier name"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Email
                      </label>
                      <input
                        type="email"
                        required
                        value={supplierFormData.email}
                        onChange={(e) => setSupplierFormData(prev => ({ ...prev, email: e.target.value }))}
                        className="w-full h-10 rounded-md border-gray-300 shadow-sm focus:border-gray-900 focus:ring-gray-900 text-sm"
                        placeholder="Enter email address"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Phone
                      </label>
                      <input
                        type="tel"
                        value={supplierFormData.phone}
                        onChange={(e) => setSupplierFormData(prev => ({ ...prev, phone: e.target.value }))}
                        className="w-full h-10 rounded-md border-gray-300 shadow-sm focus:border-gray-900 focus:ring-gray-900 text-sm"
                        placeholder="Enter phone number (optional)"
                      />
                    </div>

                    <div className="flex justify-end space-x-2">
                      <button
                        type="button"
                        onClick={() => setIsAddingSupplier(false)}
                        className="px-3 py-1.5 text-xs font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
                      >
                        Cancel
                      </button>
                      <button
                        type="button"
                        onClick={handleAddSupplier}
                        disabled={loading}
                        className="px-3 py-1.5 text-xs font-medium text-white bg-gray-900 rounded-md hover:bg-gray-800 disabled:opacity-50"
                      >
                        {loading ? 'Adding...' : 'Add Supplier'}
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {suppliers.length > 0 ? (
                      <div className="space-y-2">
                        {suppliers.map((supplier) => (
                          <button
                            key={supplier.id}
                            type="button"
                            onClick={() => setSelectedSupplier(supplier.id)}
                            className={`w-full flex items-center p-4 rounded-lg border ${
                              selectedSupplier === supplier.id
                                ? 'border-gray-900 bg-gray-50'
                                : 'border-gray-200 hover:border-gray-300'
                            }`}
                          >
                            <div className="h-10 w-10 rounded-lg bg-gray-100 flex items-center justify-center mr-4">
                              <Building2 className="h-5 w-5 text-gray-400" />
                            </div>
                            <div className="flex-1 text-left">
                              <div className="text-sm font-medium text-gray-900">
                                {supplier.first_name} {supplier.last_name}
                              </div>
                              {supplier.type === 'business' && (
                                <div className="text-sm text-gray-500">Business Supplier</div>
                              )}
                            </div>
                          </button>
                        ))}
                      </div>
                    ) : (
                      <div className="text-center py-6">
                        <Building2 className="h-8 w-8 text-gray-400 mx-auto mb-2" />
                        <p className="text-sm font-medium text-gray-900">No suppliers yet</p>
                        <p className="text-sm text-gray-500 mt-1">
                          Add your first supplier to get started
                        </p>
                      </div>
                    )}
                    <button
                      type="button"
                      onClick={() => setIsAddingSupplier(true)}
                      className="w-full flex items-center justify-center px-4 py-4 border-2 border-dashed border-gray-300 rounded-lg text-sm text-gray-600 hover:text-gray-900 hover:border-gray-400 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
                    >
                      <Plus className="h-4 w-4 mr-2" />
                      Add New Supplier
                    </button>
                  </div>
                )}
              </div>
            </div>

            <div className="mt-8">
              <button
                type="submit"
                disabled={loading}
                className="w-full px-4 py-2 text-sm font-medium text-white bg-gray-900 rounded-lg hover:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-900 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? 'Creating...' : 'Create Item'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}